/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { FlexColumn, FlexRow, Typography } from 'design-system';
import CompactProgressStepper from '../../components/CompactProgressStepper';
import LearningShell from '../../components/LearningShell';
import ProgressSidebar from '../../components/ProgressSidebar';
import QuizProgressSegments from '../../components/QuizProgressSegments';
import WarningBanner from '../../components/WarningBanner';
import GuidanceStepHeader from '../../components/GuidanceStepHeader';
import { useQuestionnaireFlow } from '../../hooks/useQuestionnaireFlow';
import QuizQuestionCard from './QuizQuestionCard';
import QuizLoadingState from './QuizLoadingState';
import '../../../Survey/pages/ClientConnectV2/client-connect-v2.css';

export default function QuestionnaireStep({ course, surveyId, onQuizComplete }) {
  const intl = useIntl();
  const flow = useQuestionnaireFlow({ course, surveyId, onQuizComplete });
  const quizTitle = intl.formatMessage(
    { id: 'ld-knowledge-check-title' },
    { title: course?.title ?? intl.formatMessage({ id: 'ld-knowledge-check-subtitle' }) },
  );

  return (
    <LearningShell
      layout="guidance"
      showWarning={false}
    >
      <FlexRow align="start" fullWidth gap="var(--ds-space-9)" className="ld-guidance-shell">
        <CompactProgressStepper steps={flow.sidebarSteps} />
        <FlexColumn align="stretch" fullWidth={false} className="ld-guidance-shell__sidebar">
          <ProgressSidebar steps={flow.sidebarSteps} />
        </FlexColumn>

        <FlexColumn align="stretch" fullWidth gap="var(--ds-space-6)" className="ld-guidance-shell__main">
          {flow.loading ? (
            <QuizLoadingState
              course={course}
              passMarkPercent={flow.passMarkPercent}
              questionCount={course?.questionCount}
            />
          ) : null}

          {!flow.loading && flow.error ? (
            <Typography component="p" variant="bodyMedium" color="ui-subtle">
              {flow.error?.startsWith('ld-')
                ? intl.formatMessage({ id: flow.error })
                : flow.error}
            </Typography>
          ) : null}

          {!flow.loading && !flow.error && !flow.submitted && flow.survey && !flow.currentQuestion ? (
            <FlexColumn align="stretch" fullWidth gap="var(--ds-space-6)" className="ld-quiz">
              <GuidanceStepHeader
                label={intl.formatMessage(
                  { id: 'ld-attempt-of' },
                  { current: flow.nextSubmitAttempt, total: flow.maxAttempts },
                )}
                title={quizTitle}
                instruction={intl.formatMessage({ id: 'ld-no-visible-questions' })}
              />
            </FlexColumn>
          ) : null}

          {!flow.loading && !flow.error && !flow.submitted && flow.currentQuestion ? (
            <FlexColumn align="stretch" fullWidth gap="var(--ds-space-6)" className="ld-quiz">
              {flow.submitError ? (
                <Typography component="p" variant="bodyMedium" color="ui-subtle">
                  {flow.submitError?.startsWith('ld-')
                    ? intl.formatMessage({ id: flow.submitError })
                    : flow.submitError}
                </Typography>
              ) : null}
              <GuidanceStepHeader
                label={intl.formatMessage(
                  { id: 'ld-attempt-of' },
                  { current: flow.nextSubmitAttempt, total: flow.maxAttempts },
                )}
                title={quizTitle}
                instruction={intl.formatMessage(
                  { id: 'ld-quiz-instruction' },
                  { count: flow.totalQuestions, passMark: flow.passMarkPercent },
                )}
              />

              <QuizProgressSegments
                total={flow.totalQuestions}
                activeIndex={flow.questionIndex}
              />

              <QuizQuestionCard
                question={flow.currentQuestion}
                questionIndex={flow.questionIndex}
                totalQuestions={flow.totalQuestions}
                answers={flow.answers}
                onSetAnswer={flow.setAnswer}
                onFinishSurvey={flow.finishSurveyEarly}
                onNext={flow.goToNextQuestion}
                onBack={flow.goToPreviousQuestion}
                canGoBack={!flow.isFirstQuestion}
                canProceed={flow.currentAnswered}
                submitting={flow.submitting}
                isLastQuestion={flow.isLastQuestion}
                passMarkPercent={flow.passMarkPercent}
                surveyErrors={flow.surveyErrors}
                onSetQuestionError={flow.setQuestionError}
                employeesLoading={flow.employeesLoading}
                survey={flow.survey}
              />
            </FlexColumn>
          ) : null}

          <WarningBanner message={intl.formatMessage({ id: 'ld-progress-warning' })} />
        </FlexColumn>
      </FlexRow>
    </LearningShell>
  );
}

QuestionnaireStep.propTypes = {
  course: PropTypes.shape({
    description: PropTypes.string,
    logoSrc: PropTypes.string,
    title: PropTypes.string,
    surveyId: PropTypes.string,
    passMarkPercent: PropTypes.number,
    questionCount: PropTypes.number,
    progressSteps: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  surveyId: PropTypes.string,
  onQuizComplete: PropTypes.func,
};
